/** The coach conversation: check-ins, skipping with a reason, the inbox, routes asked for in words,
 * and weather chips. Backend: services/coach.py, planning/feedback.py (docs/project-state.md §4). */
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  useAskRoute,
  useMe,
  useDeclineFtpResult,
  useDeclineSwap,
  useMorningCheckin,
  useRideCheckin,
  useRouteRequest,
  useSaveThresholds,
  useSkipReasons,
  useSkipSession,
  useSnoozeFoundRace,
  useStatus,
  useSwapDays,
  useUpdateSession,
  type MorningInput,
  type RideInput,
} from "@/api/hooks";
import type {
  ActivitySummary,
  Checkin,
  InboxItem,
  PlannedSession,
  SkipResult,
  SkipReason,
  Weather,
} from "@/api/schemas";
import { dayLong, hours, kindLabel, num, pct } from "@/lib/format";
import { Badge, Card, Dialog, focusOnMount } from "./ui";
import { useToast } from "./Toast";

// ------------------------------------------------------------------------------ scales
const SCALES: Record<string, { label: string; low: string; high: string }> = {
  legs: { label: "Legs", low: "heavy", high: "fresh" },
  energy: { label: "Energy", low: "flat", high: "full" },
  soreness: { label: "Soreness", low: "very sore", high: "none" },
  motivation: { label: "Motivation", low: "none", high: "can't wait" },
  sleep_quality: { label: "Sleep", low: "bad", high: "great" },
  stomach: { label: "Stomach", low: "problems", high: "fine" },
};

/** A sentence that ends with exactly one full stop (or its own ! or ?), BR-142. */
export function sentence(text: string): string {
  const t = text.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

export function ScalePicker({
  name,
  value,
  onChange,
  max = 5,
}: {
  name: string;
  value: number | null | undefined;
  onChange: (v: number) => void;
  max?: number;
}) {
  const sc = SCALES[name] ?? { label: name.toUpperCase(), low: "easy", high: "max" };
  return (
    <div className="scale" role="radiogroup" aria-label={sc.label}>
      <span className="scale-label">{sc.label}</span>
      <div className="scale-buttons">
        {Array.from({ length: max }, (_, i) => i + 1).map((v) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={value === v}
            aria-label={`${sc.label} ${v} of ${max}`}
            className={value === v ? "on" : ""}
            onClick={() => onChange(v)}
          >
            {v}
          </button>
        ))}
      </div>
      <small className="faint scale-hint">
        {sc.low} → {sc.high}
      </small>
    </div>
  );
}

export function feelScore(c: Checkin): number | null {
  const v = [c.legs, c.energy, c.soreness, c.motivation, c.sleep_quality].filter(
    (x): x is number => x != null,
  );
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
}

// ------------------------------------------------------------------------------ morning
export function MorningCheckinForm({ initial, onDone }: { initial?: Checkin | null; onDone?: () => void }) {
  const save = useMorningCheckin();
  const toast = useToast();
  const [v, setV] = useState<MorningInput>(() => ({
    legs: initial?.legs ?? null,
    energy: initial?.energy ?? null,
    ill: initial?.ill ?? false,
    injured: initial?.injured ?? false,
    note: initial?.note ?? "",
  }));
  // editing shows the day's stored reading, so a correction updates it instead of adding another (ON-20a)
  const [bp, setBp] = useState(() => ({
    sys: initial?.bp ? String(initial.bp.systolic) : "",
    dia: initial?.bp ? String(initial.bp.diastolic) : "",
    pulse: initial?.bp?.pulse != null ? String(initial.bp.pulse) : "",
  }));
  const healthData = useMe().data?.health_data ?? false;
  const set = (p: Partial<MorningInput>) => setV((x) => ({ ...x, ...p }));
  async function submit(e: FormEvent) {
    e.preventDefault();
    const n = (x: string) => (x.trim() === "" ? null : Number(x));
    const hasBp = healthData && !!bp.sys && !!bp.dia;
    const withBp = hasBp
      ? { ...v, bp_systolic: n(bp.sys), bp_diastolic: n(bp.dia), bp_pulse: n(bp.pulse) }
      : v;
    const done = (r: { plan_changes: string[] }) => {
      checkinSaved();
      toast.ok(
        r.plan_changes.length ? `Saved. ${r.plan_changes.join(" ")}` : "Saved: today's readiness updated",
      );
      onDone?.();
    };
    try {
      done(await save.mutateAsync(withBp));
    } catch (err) {
      if (!hasBp) return toast.error(err);
      // never lose the legs/energy answers because the blood-pressure part was refused
      try {
        done(await save.mutateAsync(v));
        toast.error(
          new Error(
            // the server's sentence already ends with a full stop: don't add a second one (BR-142)
            `Blood pressure not saved: ${sentence(err instanceof Error ? err.message : "check the numbers")} Your check-in is saved.`,
          ),
        );
      } catch (err2) {
        toast.error(err2);
      }
    }
  }
  return (
    <form className="stack checkin" onSubmit={submit} data-testid="morning-form">
      <div className="scales">
        {(["legs", "energy"] as const).map((k) => (
          <ScalePicker key={k} name={k} value={v[k]} onChange={(n) => set({ [k]: n })} />
        ))}
      </div>
      <div className="row" style={{ gap: 16 }}>
        <label className="check">
          <input type="checkbox" checked={!!v.ill} onChange={(e) => set({ ill: e.target.checked })} /> I feel
          ill
        </label>
        <label className="check">
          <input type="checkbox" checked={!!v.injured} onChange={(e) => set({ injured: e.target.checked })} />{" "}
          Pain / injury
        </label>
      </div>
      {!healthData ? (
        <small className="muted" data-testid="bp-needs-consent">
          Blood pressure needs the health-data consent (<Link to="/settings">Settings → Account</Link>).
        </small>
      ) : (
        <fieldset className="bp-inline" aria-label="Blood pressure (optional)">
          <legend>Blood pressure, if you measured it</legend>
          <div className="form-grid three">
            <label>
              Systolic
              <input
                inputMode="numeric"
                value={bp.sys}
                onChange={(e) => setBp({ ...bp, sys: e.target.value })}
                placeholder="mmHg"
              />
            </label>
            <label>
              Diastolic
              <input
                inputMode="numeric"
                value={bp.dia}
                onChange={(e) => setBp({ ...bp, dia: e.target.value })}
                placeholder="mmHg"
              />
            </label>
            <label>
              Pulse
              <input
                inputMode="numeric"
                value={bp.pulse}
                onChange={(e) => setBp({ ...bp, pulse: e.target.value })}
                placeholder="bpm"
              />
            </label>
          </div>
        </fieldset>
      )}
      <label>
        Anything else?
        <input value={v.note ?? ""} onChange={(e) => set({ note: e.target.value })} placeholder="optional" />
      </label>
      {(v.ill || v.injured) && (
        <small className="muted">Today becomes a rest day and the next 3 days stay easy.</small>
      )}
      <div className="row">
        <button className="primary" type="submit" disabled={save.isPending}>
          Save
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------------------------ after a ride
export function RideCheckinForm({
  activityId,
  initial,
  hours,
  onDone,
}: {
  activityId: string;
  initial?: Checkin | null;
  hours: number;
  onDone?: () => void;
}) {
  const save = useRideCheckin();
  const toast = useToast();
  const long = hours >= 2; // fuelling questions only where fuelling matters
  const [v, setV] = useState<RideInput>(() => ({
    rpe: initial?.rpe ?? null,
    legs: initial?.legs ?? null,
    stomach: initial?.stomach ?? null,
    carbs_g: initial?.carbs_g ?? null,
    fluids_l: initial?.fluids_l ?? null,
    loaded: initial?.loaded ?? null,
    note: initial?.note ?? "",
  }));
  const set = (p: Partial<RideInput>) => setV((x) => ({ ...x, ...p }));
  const numOrNull = (s: string) => (s.trim() === "" ? null : Number(s));
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync({ activityId, data: v });
      checkinSaved();
      toast.ok("Thanks: the ride review is updated");
      onDone?.();
    } catch (err) {
      toast.error(err);
    }
  }
  return (
    <form className="stack checkin" onSubmit={submit} data-testid="ride-form">
      <div className="scale" role="radiogroup" aria-label="Effort (RPE)">
        <span className="scale-label">Effort (RPE)</span>
        <div className="scale-buttons rpe">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={v.rpe === n}
              aria-label={`Effort ${n} of 10`}
              className={v.rpe === n ? "on" : ""}
              onClick={() => set({ rpe: n })}
            >
              {n}
            </button>
          ))}
        </div>
        <small className="faint scale-hint">1 very easy · 5 steady · 8 very hard · 10 max</small>
      </div>
      {long && (
        <div className="scales">
          <ScalePicker name="stomach" value={v.stomach} onChange={(n) => set({ stomach: n })} />
        </div>
      )}
      {long && (
        <div className="form-grid">
          <label>
            Carbs eaten (g)
            <input
              type="number"
              min={0}
              max={2000}
              inputMode="numeric"
              value={v.carbs_g ?? ""}
              onChange={(e) => set({ carbs_g: numOrNull(e.target.value) })}
              placeholder={hours >= 2 ? `~${Math.round(hours * 60)} for 60 g/h` : "optional"}
            />
          </label>
          <label>
            Drank (l)
            <input
              type="number"
              min={0}
              max={20}
              step={0.25}
              inputMode="decimal"
              value={v.fluids_l ?? ""}
              onChange={(e) => set({ fluids_l: numOrNull(e.target.value) })}
              placeholder="optional"
            />
          </label>
        </div>
      )}
      <label className="check">
        <input
          type="checkbox"
          checked={v.loaded === true}
          onChange={(e) => set({ loaded: e.target.checked })}
          data-testid="ride-loaded"
        />
        On the loaded bike (bags, race setup)
      </label>
      <label>
        Notes
        <input
          value={v.note ?? ""}
          onChange={(e) => set({ note: e.target.value })}
          placeholder="how it went, what you ate, anything odd"
        />
      </label>
      <div className="row">
        <button className="primary" type="submit" disabled={save.isPending}>
          Save
        </button>
      </div>
    </form>
  );
}

/** Set when a check-in form is saved: its summary takes the keyboard focus when it replaces the form, instead of
 * the focus falling back to the page (G3). Never on a plain page load. */
let focusSummaryAt = 0;
export function checkinSaved() {
  focusSummaryAt = Date.now();
}

/** Ref callback for what replaces a saved check-in form. */
export function focusIfSaved(el: HTMLElement | null) {
  // only right after a save: a save whose summary never appears (the inbox) must not take the focus later
  if (el && Date.now() - focusSummaryAt < 3000) {
    focusSummaryAt = 0;
    el.focus();
  }
}

export function CheckinSummary({ c }: { c: Checkin }) {
  const parts: string[] = [];
  if (c.rpe != null) parts.push(`RPE ${c.rpe}`);
  for (const k of ["legs", "energy", "soreness", "motivation", "sleep_quality", "stomach"] as const) {
    if (c[k] != null) parts.push(`${SCALES[k]!.label.toLowerCase()} ${c[k]}/5`);
  }
  if (c.carbs_g != null) parts.push(`${c.carbs_g} g carbs`);
  if (c.fluids_l != null) parts.push(`${num(c.fluids_l, 1)} l`);
  if (c.loaded) parts.push("loaded bike");
  return (
    <div className="stack" style={{ gap: 4 }} data-testid="checkin-summary" ref={focusIfSaved} tabIndex={-1}>
      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
        {c.ill && <Badge tone="bad">ill</Badge>}
        {c.injured && <Badge tone="warn">pain / injury</Badge>}
        <span className="muted">{parts.join(" · ") || "No details"}</span>
      </div>
      {c.note && <i className="muted">“{c.note}”</i>}
    </div>
  );
}

// ------------------------------------------------------------------------------ skipping
/** The Sunday after a week's Monday (exclusive end: the next Monday), as YYYY-MM-DD. */
function weekEnd(monday: string): string {
  const d = new Date(`${monday}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 7);
  return d.toISOString().slice(0, 10);
}

export function SkipDialog({
  session,
  onClose,
  onDescribeRoute,
}: {
  session: (Pick<PlannedSession, "id" | "kind" | "day"> & { target_hours?: number }) | null;
  onClose: () => void;
  onDescribeRoute?: (day: string) => void;
}) {
  const reasons = useSkipReasons();
  const status = useStatus();
  const skip = useSkipSession();
  const move = useUpdateSession();
  const toast = useToast();
  const [reason, setReason] = useState<SkipReason["key"] | null>(null);
  const [note, setNote] = useState("");
  const [result, setResult] = useState<SkipResult | null>(null);
  const close = () => {
    setReason(null);
    setNote("");
    setResult(null);
    onClose();
  };
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!session || !reason) return;
    try {
      setResult(await skip.mutateAsync({ id: session.id, reason, note }));
    } catch (err) {
      toast.error(err);
    }
  }
  async function moveTo(day: string) {
    if (!session) return;
    try {
      await move.mutateAsync({ id: session.id, patch: { day, status: "modified" } });
      toast.ok(`Moved to ${dayLong(day)}`);
      close();
    } catch (err) {
      toast.error(err);
    }
  }
  const sug = result?.suggestion;
  // "Skipped Sun 1 Nov. This week: 8.5 h planned instead of 10 h. Nothing moves to other days."
  const week = status.data?.this_week;
  const inWeek =
    !!session && !!week && session.day >= week.week_start && session.day < weekEnd(week.week_start);
  const hoursNow = inWeek && !status.isFetching ? week!.planned_hours : null;
  return (
    <Dialog open={!!session} onClose={close} title={session ? `Skip · ${dayLong(session.day)}` : "Skip"}>
      {result ? (
        <div className="stack" data-testid="skip-result">
          <p data-testid="skip-summary" tabIndex={-1} ref={focusOnMount}>
            <b>Skipped {session ? `${kindLabel(session.kind)}, ${dayLong(session.day)}` : "the session"}.</b>{" "}
            {hoursNow != null && session?.target_hours
              ? `This week: ${hours(hoursNow)} planned instead of ${hours(hoursNow + session.target_hours)}. `
              : ""}
            Nothing moves to other days.
          </p>
          <p className="muted">{result.reaction}</p>
          {sug?.action === "move" && (
            <button className="primary" onClick={() => void moveTo(sug.day)}>
              Move it to {dayLong(sug.day)}
            </button>
          )}
          {sug?.action === "describe_route" && onDescribeRoute && (
            <button
              className="primary"
              onClick={() => {
                onDescribeRoute(sug.day);
                close();
              }}
            >
              Describe a route where you are
            </button>
          )}
          <button onClick={close}>Done</button>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          <fieldset className="reasons" aria-label="Why?">
            {(reasons.data ?? []).map((r) => (
              <label key={r.key} className={`reason ${reason === r.key ? "on" : ""}`}>
                <input
                  type="radio"
                  name="skip-reason"
                  value={r.key}
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                />
                <b>{r.label}</b>
                <small className="faint">{r.effect}</small>
              </label>
            ))}
          </fieldset>
          <label>
            Note
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" />
          </label>
          <button className="primary" type="submit" disabled={!reason || skip.isPending}>
            Skip session
          </button>
        </form>
      )}
    </Dialog>
  );
}

export const SKIP_LABEL: Record<string, string> = {
  sick: "sick",
  injury: "injury",
  tired: "tired",
  work: "work",
  family: "family",
  travel: "travel",
  weather: "weather",
  other: "other",
};

// ------------------------------------------------------------------------------ inbox
export function CoachCard({
  inbox,
  today,
  checkinToday,
  lastActivity,
  onDescribeRoute,
}: {
  inbox: InboxItem[];
  today: string;
  checkinToday: Checkin | null;
  lastActivity: (ActivitySummary & { checkin?: Checkin | null }) | null;
  onDescribeRoute: (day: string) => void;
}) {
  const [idx, setIdx] = useState(0);
  const [skipping, setSkipping] = useState<{ id: number; kind: PlannedSession["kind"]; day: string } | null>(
    null,
  );
  const [editMorning, setEditMorning] = useState(false);
  const fs = checkinToday ? feelScore(checkinToday) : null;
  const openSkip = (it: InboxItem) => {
    if (it.session_id != null) setSkipping({ id: it.session_id, kind: "endurance", day: it.day });
  };
  return (
    <Card title="Coach" className="coach-card">
      <div className="stack" data-testid="coach-inbox">
        {(inbox.length === 0 || checkinToday) && !editMorning && (
          <p
            className="muted"
            style={{ margin: 0 }}
            tabIndex={-1}
            ref={checkinToday ? focusIfSaved : undefined}
          >
            {inbox.length === 0 && "All caught up. "}
            {checkinToday && (
              <>
                Today you feel {fs != null ? <b>{num(fs, 1)}/5</b> : "noted"}.{" "}
                <button className="ghost small" onClick={() => setEditMorning(true)}>
                  Change
                </button>
              </>
            )}
          </p>
        )}
        {editMorning && <MorningCheckinForm initial={checkinToday} onDone={() => setEditMorning(false)} />}
        {inbox.length > 0 && (
          <CoachQuestion
            key={key(inbox[Math.min(idx, inbox.length - 1)]!)}
            it={inbox[Math.min(idx, inbox.length - 1)]!}
            step={Math.min(idx, inbox.length - 1) + 1}
            total={inbox.length}
            today={today}
            checkinToday={checkinToday}
            lastActivity={lastActivity}
            onSkip={openSkip}
            onLater={() => setIdx((i) => (i + 1) % inbox.length)}
          />
        )}
      </div>
      <SkipDialog session={skipping} onClose={() => setSkipping(null)} onDescribeRoute={onDescribeRoute} />
    </Card>
  );
}

const key = (it: InboxItem) => `${it.kind}:${it.activity_id ?? it.session_id ?? it.day}`;

/** Confirm the FTP from this month's test (prefilled with 75 % of the best minute). */
function FtpResultForm({ it }: { it: InboxItem }) {
  const save = useSaveThresholds();
  const decline = useDeclineFtpResult();
  const toast = useToast();
  const [w, setW] = useState(it.ftp?.proposed_w != null ? String(it.ftp.proposed_w) : "");
  const busy = save.isPending || decline.isPending;
  const n = Number(w);
  return (
    <>
      <label className="ftp-input">
        FTP (W)
        <input
          type="number"
          inputMode="numeric"
          min={80}
          max={600}
          value={w}
          onChange={(e) => setW(e.target.value)}
          aria-label="New FTP in watts"
        />
      </label>
      <button
        className="primary small"
        disabled={busy || !(n >= 80 && n <= 600)}
        onClick={() =>
          save.mutateAsync({ ftp_w: Math.round(n), valid_from: it.day }).then(
            () => toast.ok(`FTP ${Math.round(n)} W saved; power targets follow`),
            (e: unknown) => toast.error(e),
          )
        }
      >
        Save FTP
      </button>
      <button
        className="small"
        disabled={busy}
        onClick={() =>
          decline.mutateAsync(it.session_id!).then(
            () => toast.ok("Kept the current FTP"),
            (e: unknown) => toast.error(e),
          )
        }
      >
        Keep current
      </button>
    </>
  );
}

function WeatherSwapButtons({ it }: { it: InboxItem }) {
  const swap = useSwapDays();
  const decline = useDeclineSwap();
  const toast = useToast();
  const busy = swap.isPending || decline.isPending;
  return (
    <>
      <button
        className="primary small"
        disabled={busy}
        onClick={() =>
          swap.mutateAsync({ a: it.day, b: it.to_day!, weather: true }).then(
            () => toast.ok("Rides swapped. Pick routes for the new days on Today."),
            (e: unknown) => toast.error(e),
          )
        }
      >
        Swap the days
      </button>
      <button
        className="small"
        disabled={busy}
        onClick={() =>
          decline.mutateAsync(it.day).then(
            () => toast.ok("Kept as planned"),
            (e: unknown) => toast.error(e),
          )
        }
      >
        Keep as planned
      </button>
    </>
  );
}

function FoundRaceButtons() {
  const snooze = useSnoozeFoundRace();
  const toast = useToast();
  return (
    <>
      <Link
        className="btn primary small"
        to="/plan#events"
        // pushState (the router's) fires no hashchange: tell the Plan page, if it is the one showing, to read it
        onClick={() => setTimeout(() => window.dispatchEvent(new HashChangeEvent("hashchange")), 0)}
      >
        Add a race
      </Link>
      <button
        className="small"
        disabled={snooze.isPending}
        onClick={() =>
          snooze.mutateAsync().then(
            () => toast.ok("OK, I'll ask again in a month"),
            (e: unknown) => toast.error(e),
          )
        }
      >
        Not yet
      </button>
    </>
  );
}

function CoachQuestion({
  it,
  step,
  total,
  today,
  checkinToday,
  lastActivity,
  onSkip,
  onLater,
}: {
  it: InboxItem;
  step: number;
  total: number;
  today: string;
  checkinToday: Checkin | null;
  lastActivity: (ActivitySummary & { checkin?: Checkin | null }) | null;
  onSkip: (it: InboxItem) => void;
  onLater: () => void;
}) {
  const rideHours =
    lastActivity && lastActivity.id === it.activity_id
      ? (lastActivity.moving_s ?? lastActivity.elapsed_s) / 3600
      : 2;
  return (
    <div className={`inbox-item ${it.kind}`} data-testid="coach-question">
      <div className="row between" style={{ alignItems: "baseline" }}>
        <b>{it.title}</b>
        {total > 1 && (
          <span className="coach-step">
            {step} of {total}
          </span>
        )}
      </div>
      <small className="muted">{it.detail}</small>
      {it.kind === "morning_checkin" && (
        <MorningCheckinForm initial={it.day === today ? checkinToday : null} />
      )}
      {it.kind === "ride_checkin" && it.activity_id && (
        <RideCheckinForm activityId={it.activity_id} hours={rideHours} />
      )}
      <div className="row" style={{ marginTop: 10, gap: 8 }}>
        {it.kind === "missed_session" && (
          <button className="primary small" onClick={() => onSkip(it)}>
            Tell the coach why
          </button>
        )}
        {it.kind === "weather_swap" && it.to_day && <WeatherSwapButtons it={it} />}
        {it.kind === "ftp_result" && it.session_id != null && <FtpResultForm it={it} />}
        {it.kind === "found_race" && <FoundRaceButtons />}
        {total > 1 && (
          <button className="ghost small" onClick={onLater}>
            Later
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------ route in words
const EXAMPLES = ["3 h from home, mostly gravel", "tomorrow 80 km, hilly", "easy 1.5 h, flat, road is fine"];

export function RouteRequestBox({ day, autoFocus = false }: { day: string; autoFocus?: boolean }) {
  const last = useRouteRequest(day);
  const ask = useAskRoute();
  const toast = useToast();
  const [text, setText] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (text.trim().length < 2) return;
    try {
      const r = await ask.mutateAsync({ text });
      toast.ok(`${r.routes.length} routes: ${r.request.interpretation}`);
      setText("");
    } catch (err) {
      toast.error(err);
    }
  }
  const req = ask.data?.request ?? last.data ?? null;
  return (
    <form className="stack route-request" onSubmit={submit} data-testid="route-request">
      <label>
        Want something different? Describe the route
        <textarea
          rows={2}
          value={text}
          autoFocus={autoFocus}
          onChange={(e) => setText(e.target.value)}
          placeholder={EXAMPLES[0]}
          maxLength={500}
        />
      </label>
      <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {EXAMPLES.map((ex) => (
            <button key={ex} type="button" className="ghost small chip" onClick={() => setText(ex)}>
              {ex}
            </button>
          ))}
        </div>
        <button className="primary small" type="submit" disabled={ask.isPending || text.trim().length < 2}>
          {ask.isPending ? "Routing… (~1 min)" : "Find routes"}
        </button>
      </div>
      {req && (
        <div className="request-result" data-testid="route-request-result">
          <small className="muted">
            Understood: <b>{req.interpretation}</b>
          </small>
          {req.notes.map((n) => (
            <small key={n} className="faint" style={{ display: "block" }}>
              {n}
            </small>
          ))}
        </div>
      )}
    </form>
  );
}

// ------------------------------------------------------------------------------ weather
export function WeatherChip({ w, warnings = [] }: { w: Weather | null; warnings?: string[] }) {
  if (!w) return null;
  return (
    <div className="weather" data-testid="weather">
      <span className="muted">
        {num(w.tmin)}–{num(w.tmax)}°C · rain {pct(w.precip_prob)} · wind {num(w.wind_max_kmh)} km/h
      </span>
      {warnings.map((x) => (
        <small key={x} className="warn-text">
          {x}
        </small>
      ))}
    </div>
  );
}

export function RideWeather({ w }: { w: ActivitySummary["weather"] }) {
  if (!w) return null;
  return (
    <span className="muted" data-testid="ride-weather">
      {num(w.temp_min_c)}–{num(w.temp_max_c)}°C
      {w.precip_mm ? ` · ${num(w.precip_mm, 1)} mm rain` : " · dry"}
      {w.wind_kmh != null && ` · wind ${num(w.wind_kmh)} km/h`}
    </span>
  );
}
